import {
  Inject,
  Injectable,
  Injector
} from '@angular/core';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import {
  AddChartDialogComponent,
  CompareDateDialogComponent,
  CompareSymbolDialogComponent
} from '../components';
import {
  concatMapTo,
  take
} from 'rxjs/operators';
import {
  CardTypeEnum,
  ICardChartData,
  IChangeIVDiffStockSeries,
  IChangeStockSeries,
  IChartDialog,
  ICompareDateDialog,
  ICompareDialogOutput,
  ICompareSymbol,
  ILayout,
  SymbolDialogActionEnum
} from '../interfaces';
import { TuiDialogService } from '@taiga-ui/core';
import { ChartsCommonService } from './charts-common.service';
import {
  ChartsDashboardQuery,
  IChartDashboard
} from '../state/dashboard';
import {
  ChartsDashboardCardQuery,
  ICard
} from '../state/card';
import { ChartsDashboardStateService } from '../state/charts-dashboard-state.service';
import { IChartSeries } from 'app/charts-dashboard/state/series';

@Injectable()
export class ChartsDialogsService {

  constructor(
    private readonly chartsState: ChartsDashboardStateService,
    private readonly dashboardQuery: ChartsDashboardQuery,
    private readonly cardsQuery: ChartsDashboardCardQuery,
    private readonly chartsCommonService: ChartsCommonService,
    @Inject(TuiDialogService) private readonly dialogService: TuiDialogService
  ) {
  }

  public createAddChartDialog(injector: Injector, data: IChartDialog): void {
    this.dialogService.open(new PolymorpheusComponent(AddChartDialogComponent, injector), {data, size: 's'})
      .pipe(
        concatMapTo(
          this.dashboardQuery.selectDashboard(this.dashboardQuery.getCurrentDashboardId),
          (outerValue, innerValue) => {
            return {
              complete: outerValue,
              dashboard: innerValue,
            };
          }
        ),
        take(1)
      )
      .subscribe((value: { dashboard: IChartDashboard<ICard<IChartSeries>>, complete: ICardChartData }) => {
        this.createAndFillCard(value.complete, value.dashboard);
      });
  }

  public createCompareSymbolDialog(injector: Injector, data: (IChangeStockSeries | ICompareSymbol | IChangeIVDiffStockSeries)): void {
    this.dialogService
      .open(new PolymorpheusComponent(CompareSymbolDialogComponent, injector), {data, size: 's'})
      .subscribe((complete: ICompareDialogOutput) => this.compareEntity(complete));
  }

  public createCompareDateDialog(injector: Injector, data: ICompareDateDialog): void {
    this.dialogService
      .open(new PolymorpheusComponent(CompareDateDialogComponent, injector), {data, size: 's'})
      .subscribe((complete: ICompareDialogOutput) => this.compareEntity(complete));
  }

  private createAndFillCard(chartData: ICardChartData, dashboard: IChartDashboard<ICard<IChartSeries>>): void {
    let addCard: ICard<IChartSeries> = dashboard.cards.find((card: ICard<IChartSeries>) => card.type === CardTypeEnum.ADD_NEW_CARD);
    const voidCard: ICard<IChartSeries> = dashboard.cards.find((card: ICard<IChartSeries>) => card.type === CardTypeEnum.VOID);

    if (
      !addCard &&
      dashboard.layout.cardsAmount === dashboard.cards.length &&
      dashboard.layout.cardsAmount < 8
    ) {
      const newLayout: ILayout = this.chartsState.layouts.find((layout: ILayout) => layout.cardsAmount > dashboard.layout.cardsAmount);

      this.chartsState.selectChartLayout(newLayout.layoutEntity[0].name);
      dashboard = this.dashboardQuery.getDashboardSnapshot(this.dashboardQuery.getCurrentDashboardId);

      addCard = dashboard.cards.find((card: ICard<IChartSeries>) => card.type === CardTypeEnum.ADD_NEW_CARD);
    }

    if (voidCard) {
      this.chartsState.updateCard(voidCard.id, {type: CardTypeEnum.ADD_NEW_CARD});
    }

    this.chartsState.fillCard(addCard.id, chartData, dashboard.id);
  }

  private compareEntity(chartData: ICompareDialogOutput): void {
    switch (chartData.action) {
      case SymbolDialogActionEnum.COMPARE_SERIES:
        this.chartsState.compareSeries(chartData);
        break;
      case SymbolDialogActionEnum.CHANGE_STOCK_IN_COMPARE_SERIES:
      case SymbolDialogActionEnum.CHANGE_STOCK_IN_MAIN_SERIES:
        this.chartsState.changeStockInSeries(chartData);
        break;
      case SymbolDialogActionEnum.CHANGE_IV_DIFF_STOCK_IN_SERIES:
        this.chartsState.changeIVDiffStock(chartData.seriesId, chartData.selectedFilters);
        break;
      case SymbolDialogActionEnum.CHANGE_STOCK_IN_ALL_SERIES:
        this.chartsState.changeStockInAllSeriesCard(this.cardsQuery.getEntity(chartData.parentCardId), chartData.stock);
        break;
      case SymbolDialogActionEnum.COMPARE_DATE:
        this.chartsState.compareSeries(chartData);
        break;
      case SymbolDialogActionEnum.CHANGE_STOCK_IN_INTRADAY_CHART:
        this.chartsState.changeStockInIntraDayChart(chartData);
        break;
    }
  }
}
